import type { BomaidRegistrationModel, RegisterModel } from 'src/app/api/proxy/vitalscore/vitalscore-models';
import { sharedConfig } from 'src/environments/shared-config';

/**
 * Profile + app context from profile-detail / OTP flow (password may not be set yet on create-password).
 * Pure helpers (no Angular DI) so they can be unit-tested without loading the full RegistrationService module.
 */
export function isStandardRegistrationProfileComplete(model: RegisterModel | null | undefined): boolean {
  if (!model) {
    return false;
  }
  const email = (model.emailAddress ?? '').trim();
  const given = (model.givenName ?? '').trim();
  const surname = (model.surname ?? '').trim();
  const dob = (model.dateOfBirth ?? '').toString().trim();
  const applicationId = (model.applicationId ?? '').toString().trim();
  if (!email || !email.includes('@') || !given || !surname || !dob || !applicationId) {
    return false;
  }
  if (model.gender === null || model.gender === undefined) {
    return false;
  }
  return true;
}

/** Full payload for RegisterProfile (includes password set on create-password). */
export function isStandardRegistrationDataComplete(model: RegisterModel | null | undefined): boolean {
  if (!isStandardRegistrationProfileComplete(model)) {
    return false;
  }
  return !!(model!.password ?? '').trim();
}

/**
 * Bomaid registration needs standard fields plus policy number.
 * Used to block Preferences race / empty CVA submits that produced blank API posts.
 */
export function isBomaidRegistrationDataComplete(model: RegisterModel | null | undefined): boolean {
  if (!isStandardRegistrationDataComplete(model)) {
    return false;
  }
  const policyNumber = String((model as { policyNumber?: string } | null | undefined)?.policyNumber ?? '').trim();
  return !!policyNumber;
}

/**
 * Bomaid registration page always uses flavour environment.organizationId (parent org) — no cache fallback.
 */
export function resolveBomaidRegistrationOrganizationId(
  environmentOrganizationId: string | null | undefined
): string | undefined {
  const fromEnv = (environmentOrganizationId ?? '').trim();
  return fromEnv || undefined;
}

export type BomaidRegistrationUserFields = RegisterModel & {
  policyNumber?: string;
  isMainMember?: boolean;
};

export interface BuildBomaidRegistrationModelInput {
  applicationId: string;
  applicationScope: string;
  languageCode: string;
  /** Must be environment.organizationId (forced; never a cached/default org). */
  environmentOrganizationId: string | null | undefined;
  user: BomaidRegistrationUserFields;
}

/**
 * Builds the Bomaid API registration body. Returns null when environment.organizationId is missing
 * so callers never POST without registrationOrganizationId.
 */
export function buildBomaidRegistrationModel(
  input: BuildBomaidRegistrationModelInput
): BomaidRegistrationModel | null {
  const registrationOrganizationId = resolveBomaidRegistrationOrganizationId(input.environmentOrganizationId);
  if (!registrationOrganizationId) {
    return null;
  }

  return {
    applicationId: input.applicationId,
    applicationScope: input.applicationScope,
    dateOfBirth: input.user.dateOfBirth!,
    emailAddress: input.user.emailAddress ?? '',
        languageCode: input.languageCode || sharedConfig.defaultCulture,
    password: input.user.password ?? '',
    policyNumber: String(input.user.policyNumber ?? '').trim(),
    surname: input.user.surname!,
    givenName: input.user.givenName,
    gender: input.user.gender,
    registrationOrganizationId,
    isMainMember: input.user.isMainMember,
    mobileNumber: input.user.mobileNumber,
  };
}
